/**
 * BlogArticleDetail.js - Public blog article detail page
 * Route: /blog/:slug
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import Logo from './Logo';
import { getBlogArticle } from '../services/api';
import './BlogArticleDetail.css';

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric'
  });
}

function BlogArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState('');
  const metaDescRef = useRef(null);

  useEffect(() => {
    loadArticle();
    return () => {
      // Restore defaults on unmount
      document.title = 'LeagueMaker';
      if (metaDescRef.current) {
        metaDescRef.current.setAttribute('content', 'Create balanced league schedules in minutes.');
      }
    };
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadArticle = async () => {
    setLoading(true);
    setNotFound(false);
    setError('');
    try {
      const data = await getBlogArticle(slug);
      setArticle(data);
      document.title = `${data.title} | LeagueMaker`;

      // Update meta description for SEO
      let metaEl = document.querySelector('meta[name="description"]');
      if (!metaEl) {
        metaEl = document.createElement('meta');
        metaEl.setAttribute('name', 'description');
        document.head.appendChild(metaEl);
      }
      metaDescRef.current = metaEl;
      metaEl.setAttribute('content', data.excerpt || data.title);
    } catch (err) {
      if (err.message === 'Article not found') {
        setNotFound(true);
      } else {
        setError('Failed to load article. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    if (loading) return <div className="blog-detail-loading">Loading...</div>;
    if (notFound) return (
      <div className="blog-detail-not-found">
        <p>Article not found.</p>
        <Link to="/blog" className="blog-detail-back">← Back to Blog</Link>
      </div>
    );
    if (error) return <div className="blog-detail-error">{error}</div>;
    if (!article) return null;

    const htmlContent = DOMPurify.sanitize(marked.parse(article.content || ''));

    return (
      <>
        <Link to="/blog" className="blog-detail-back">← Back to Blog</Link>
        <div className="blog-detail-meta">
          {formatDate(article.publishedAt)}
          {article.author?.name && ` · ${article.author.name}`}
        </div>
        <h1 className="blog-detail-title">{article.title}</h1>
        {article.excerpt && (
          <p className="blog-detail-excerpt">{article.excerpt}</p>
        )}
        <hr className="blog-detail-divider" />
        <div
          className="blog-detail-body"
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
      </>
    );
  };

  return (
    <div className="blog-detail-page">
      <nav className="blog-detail-nav">
        <Link to="/" className="blog-detail-logo-link">
          <Logo size="1.5rem" /> LeagueMaker
        </Link>
        <Link to="/blog" className="blog-detail-nav-link">Blog</Link>
      </nav>

      <article className="blog-detail-article">
        {renderContent()}
      </article>

      <footer className="blog-detail-footer">
        <div style={{ marginBottom: '0.4rem' }}>
          <Link to="/terms">Terms</Link>
          <span className="blog-detail-footer-divider">|</span>
          <Link to="/privacy">Privacy</Link>
          <span className="blog-detail-footer-divider">|</span>
          <Link to="/pricing">Pricing</Link>
        </div>
        <div>© {new Date().getFullYear()} LeagueMaker</div>
      </footer>
    </div>
  );
}

export default BlogArticleDetail;
